import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SideBar from './SideBar';
import AnimatedBackground from './AnimatedBackground';
import UserAvatar from './UI/UserAvatar';
import FloatingBallsBackground from './UI/FloatingBallsBackground';
import AnimatedLinesBackground from './UI/AnimatedLinesBackground';
import { useTheme, BACKGROUND_TYPES } from '../contexts/ThemeContext';
import { AuthService } from '../services/authService';

const AuthenticatedLayout = ({ children }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { backgroundType } = useTheme();

  useEffect(() => {
    // Detect mobile devices
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     window.innerWidth < 768;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = async () => {
    // AuthService.logout() POSTs to /auth/logout, clears stored tokens and
    // localStorage, then redirects. It also force-clears on failure, so we
    // don't need a try/catch fallback here.
    await AuthService.logout();
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const renderBackground = () => {
    // On mobile, use simple static gradient instead of animated backgrounds
    if (isMobile) {
      return (
        <div
          className="fixed inset-0 -z-10"
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #581c87 50%, #0f172a 100%)'
          }}
        />
      );
    }

    // Desktop: use full animated backgrounds
    switch (backgroundType) {
      case BACKGROUND_TYPES.BALLS:
        return <FloatingBallsBackground />
      case BACKGROUND_TYPES.LINES:
        return <AnimatedLinesBackground />
      case BACKGROUND_TYPES.GLASS:
      default:
        return <AnimatedBackground />
    }
  }

  return (
    <div className="min-h-screen relative">
      {renderBackground()}
      
      {/* Top Navigation Bar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 border-b border-white/10 ${
          isMobile ? 'bg-slate-900/95' : 'backdrop-blur-xl'
        }`}
        style={isMobile ? {} : {backgroundColor: 'transparent', backdropFilter: 'blur(20px)'}}
      >
        <div className="flex items-center justify-between px-6 py-4">
          {/* Left side - Hamburger menu and logo */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="text-white hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-white/10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            {/* Logo — doubles as the home link, which is where people
                instinctively click first in every other app. */}
            <button
              type="button"
              onClick={() => navigate('/home')}
              aria-label="Go to home"
              title="Home"
              className="flex items-center gap-2 rounded-lg px-2 py-1 -mx-2 hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
            >
              <UserAvatar size="sm" className="rounded-lg" />
              <span className="font-semibold text-lg" style={{color: 'var(--theme-text-primary)'}}>MainStage</span>
            </button>
          </div>

        </div>
      </nav>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className={`fixed inset-0 ${isMobile ? 'bg-black/80' : 'bg-black/50 backdrop-blur-sm'} z-40`}
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 h-full z-50 transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <SideBar onLogout={handleLogout} onClose={toggleSidebar} />
      </div>

      {/* Main Content */}
      <main className="pt-20">
        {children}
      </main>
    </div>
  );
};

export default AuthenticatedLayout;